/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createOrganizationProjectEnvironment, createReleaseChannel } from '../../api';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type EnvironmentDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function EnvironmentDialog({ actionDialogReturn, onNavigate: _onNavigate }: EnvironmentDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { toast } = useFeedback();
  const {
    activeOrganization,
    activeProject,
    releaseVersionOptions,
    setWorkspaceVersion,
  } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'environment') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'environment' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      if (activeOrganization && activeProject) {
        const newEnv = await createOrganizationProjectEnvironment(
          session.accessToken,
          activeOrganization.id,
          activeProject.id,
          {
            name: actionDialog.name.trim(),
            slug: actionDialog.slug.trim() || undefined,
            region: actionDialog.region.trim() || undefined
          }
        );
        const otaEnvs = ['dev', 'staging', 'production', 'internal'] as const;
        if ((otaEnvs as readonly string[]).includes(newEnv.slug)) {
          await Promise.all(
            releaseVersionOptions.map((version) =>
              createReleaseChannel(session.accessToken, {
                organizationId: activeOrganization.id,
                projectId: activeProject.id,
                tenantEnvironmentId: newEnv.tenantEnvironmentId,
                environment: newEnv.slug as typeof otaEnvs[number],
                channel: version.version,
                isActive: true
              }).catch(() => null)
            )
          );
        }
        setWorkspaceVersion((v) => v + 1);
      }
      toast('success', 'Environment created');
      setActionDialog(null);
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Environment</div>
            <h3>New environment</h3>
            <p>Create a project-scoped runtime target for OTA and runtime config.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder="Preview"
            />
          </label>
          <label className="settingsField">
            <span>Slug</span>
            <input
              value={actionDialog.slug}
              onChange={(e) => setActionDialog({ ...actionDialog, slug: e.target.value })}
              placeholder="preview"
            />
          </label>
          <label className="settingsField">
            <span>Region</span>
            <input
              value={actionDialog.region}
              onChange={(e) => setActionDialog({ ...actionDialog, region: e.target.value })}
              placeholder="us"
            />
          </label>
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy}>
            {dialogBusy ? 'Creating…' : 'Create environment'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { EnvironmentDialog };
